import { Dispatch, FC, MouseEvent, SetStateAction } from 'react'
import { useNavigate } from 'react-router-dom'
import './performanceScoreChart.styles.css'
import { Typography } from '@mui/material'
import { useAppSelector } from 'store/hooks'
import { selectThemeMode } from 'store/theme/themeSlice'

/* Utils */
import { IChartData, PerformanceStatus } from '../../../../../models'

/* Components */
import { PizzaChartWrapper, SetupWrapper } from './performanceScoreChart.styles'
import { RightIconButton } from '../../../../components/svg'
import { ThemeButton } from '../../../../components/buttons'
import PerformanceScoreTooltip, { PerformanceScoreTooltipProps } from './PerformanceScoreTooltip'

const NUM_RINGS = 5
const SIZE = 240
const CENTER = SIZE / 2
const RADIUS = 116

interface Props {
  data: IChartData[]
  noCategory: boolean
  tooltip: PerformanceScoreTooltipProps
  setTooltip: Dispatch<SetStateAction<PerformanceScoreTooltipProps>>
  overperformanceIconTooltip: PerformanceScoreTooltipProps
  setOverperformanceIconTooltip: Dispatch<SetStateAction<PerformanceScoreTooltipProps>>
  handleCategoryClick: (category: string) => void
  setHoveredCard: Dispatch<SetStateAction<string>>
}

/* Point on the circle, measured from 12 o'clock going clockwise (matches d3.pie order). */
const point = (r: number, angle: number): [number, number] => [
  CENTER + r * Math.sin(angle),
  CENTER - r * Math.cos(angle)
]

/* Pie wedge path (from the center) between two angles. */
const wedge = (r: number, a0: number, a1: number): string => {
  const [x0, y0] = point(r, a0)
  const [x1, y1] = point(r, a1)
  const largeArc = a1 - a0 > Math.PI ? 1 : 0
  return `M ${CENTER} ${CENTER} L ${x0} ${y0} A ${r} ${r} 0 ${largeArc} 1 ${x1} ${y1} Z`
}

/* Open arc (no center lines) between two angles — used for the guide rings. */
const arc = (r: number, a0: number, a1: number): string => {
  const [x0, y0] = point(r, a0)
  const [x1, y1] = point(r, a1)
  const largeArc = a1 - a0 > Math.PI ? 1 : 0
  return `M ${x0} ${y0} A ${r} ${r} 0 ${largeArc} 1 ${x1} ${y1}`
}

const PerformanceScoreChart: FC<Props> = ({
  data,
  noCategory,
  tooltip,
  setTooltip,
  setOverperformanceIconTooltip,
  handleCategoryClick,
  setHoveredCard
}) => {
  const navigate = useNavigate()
  const isLight = useAppSelector(selectThemeMode) === 'light'

  const pinkStart = isLight ? '#FD71CE' : '#FFA9FC'
  const pinkStartOp = isLight ? 0.2 : 1
  const pinkEnd = isLight ? '#A13B7F' : '#FFA9FC'
  const pinkEndOp = isLight ? 0.8 : 0.4
  const pinkStroke = isLight ? '#A13B7F' : '#FFA9FC'
  const blue = isLight ? '#CACAF8' : '#3754FC'
  const blueStartOp = 1
  const blueEndOp = 0.4
  const ringColor = isLight ? '#e7e7ec' : '#998d96'
  /* Dark needs the rings held back over an empty slice; on the light card they already read as faint. */
  const ringOpacityEmpty = isLight ? 1 : 0.4
  /* Figma leaves these at the SVG default of 1; dark reads better held to a hairline. */
  const ringWidth = isLight ? 1 : 0.5
  /* Near-transparent so an empty slice reads as a gap in the chart rather than a filled wedge. */
  const unavailableFill = isLight ? 'rgba(220, 210, 217, 0.08)' : 'rgba(77, 68, 75, 0.08)'
  const triangleStroke = isLight ? '#18181f' : '#ffffff'

  const showSetup = noCategory || data.every((n) => n.score === 100)
  const anglePer = (2 * Math.PI) / data.length

  const showTooltip = (e: MouseEvent, d: IChartData) => {
    if (d.emptySlice) return
    const content = d.status === PerformanceStatus.unavailable ? { ...d, score: 0 } : d
    setTooltip({ x: e.pageX, y: e.pageY, content })
    setHoveredCard(d.category)
  }
  const hideTooltip = () => {
    setTooltip({ x: 0, y: 0, content: null })
    setHoveredCard('')
  }
  const handleClick = (d: IChartData) => {
    if (d.score === 0) handleCategoryClick(d.category)
  }

  /* Pink "performance" wedges — length = score, each with its own gradient scaled to its radius (like Figma). */
  const pinkWedges = data
    .map((d, i) => ({
      d,
      i,
      r: d.emptySlice || d.status === PerformanceStatus.unavailable ? 0 : RADIUS * Math.min(d.score / 100, 1.039)
    }))
    .filter((w) => w.r > 0)

  return (
    <PizzaChartWrapper>
      {showSetup ? (
        <>
          <SetupWrapper>
            <Typography className="setup-title">Activate a CPI to Begin</Typography>
            <ThemeButton onClick={() => navigate('/library')}>
              <>
                <RightIconButton />
                Explore CPIs
              </>
            </ThemeButton>
          </SetupWrapper>
          <img src="/blur-pizza.png" alt="pizza" />
        </>
      ) : (
        <svg width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`} className="performance-score-chart-svg">
          <defs>
            <radialGradient id="ps-blue" gradientUnits="userSpaceOnUse" cx={CENTER} cy={CENTER} r={RADIUS}>
              <stop offset="0%" stopColor={blue} stopOpacity={blueStartOp} />
              <stop offset="100%" stopColor={blue} stopOpacity={blueEndOp} />
            </radialGradient>
            {pinkWedges.map(({ i, r }) => (
              <radialGradient
                key={`grad-${i}`}
                id={`ps-pink-${i}`}
                gradientUnits="userSpaceOnUse"
                cx={CENTER}
                cy={CENTER}
                r={r}
              >
                <stop offset="0%" stopColor={pinkStart} stopOpacity={pinkStartOp} />
                <stop offset="100%" stopColor={pinkEnd} stopOpacity={pinkEndOp} />
              </radialGradient>
            ))}
          </defs>

          {/* concentric guide rings — under the wedges, so the wedge fills occlude them as in Figma */}
          {data.map((d, i) =>
            Array.from({ length: NUM_RINGS }).map((_, k) => {
              const unavailable = d.score === 0 || d.status === PerformanceStatus.unavailable
              return (
                <path
                  key={`ring-${i}-${k}`}
                  d={arc((RADIUS * (k + 1)) / NUM_RINGS, i * anglePer, (i + 1) * anglePer)}
                  fill="none"
                  stroke={ringColor}
                  strokeOpacity={unavailable ? ringOpacityEmpty : 1}
                  strokeWidth={ringWidth}
                  strokeDasharray="2 2"
                  pointerEvents="none"
                />
              )
            })
          )}

          {/* blue "potential" wedges (full radius) */}
          {data.map((d, i) => {
            const unavailable = d.score === 0 || d.status === PerformanceStatus.unavailable
            return (
              <path
                key={`blue-${i}`}
                d={wedge(RADIUS, i * anglePer, (i + 1) * anglePer)}
                fill={unavailable ? unavailableFill : 'url(#ps-blue)'}
                stroke={unavailable ? 'none' : blue}
                strokeWidth={0.5}
                style={{ cursor: d.score === 0 ? 'pointer' : 'default' }}
                onMouseEnter={(e) => showTooltip(e, d)}
                onMouseLeave={hideTooltip}
                onClick={() => handleClick(d)}
              />
            )
          })}

          {/* pink "performance" wedges */}
          {pinkWedges.map(({ d, i, r }) => (
            <path
              key={`pink-${i}`}
              d={wedge(r, i * anglePer, (i + 1) * anglePer)}
              fill={`url(#ps-pink-${i})`}
              stroke={pinkStroke}
              strokeWidth={0.5}
              style={{ cursor: 'pointer' }}
              onMouseEnter={(e) => showTooltip(e, d)}
              onMouseLeave={hideTooltip}
              onClick={() => handleClick(d)}
            />
          ))}

          {/* over-performance markers (score > 100) — Figma arrow, spaced off the chart, pointing at its slice */}
          {data.map((d, i) => {
            if (d.emptySlice || d.score <= 100) return null
            const mid = (i + 0.5) * anglePer
            /* Clears the 1.039x over-performance wedge; the svg overflows the column so it lands in the gap. */
            const [baseX, baseY] = point(RADIUS + 16, mid)
            const midDeg = (mid * 180) / Math.PI
            return (
              <g
                key={`over-${i}`}
                transform={`translate(${baseX - 8}, ${baseY - 1}) rotate(${midDeg + 180}, 8, 1)`}
                style={{ cursor: 'pointer' }}
                onMouseEnter={(e) => {
                  setOverperformanceIconTooltip({ x: e.pageX, y: e.pageY, content: d })
                  setHoveredCard(d.category)
                }}
                onMouseLeave={() => {
                  setOverperformanceIconTooltip({ x: 0, y: 0, content: null })
                  setHoveredCard('')
                }}
              >
                <path
                  d="M13.3173 1.63311C14.45 1.74718 15.0479 3.03252 14.4056 3.97243L8.07303 13.2366C7.41688 14.1963 5.96573 14.0774 5.47508 13.0235L0.641955 2.63471C0.15151 1.5805 0.995224 0.393212 2.15201 0.509389L13.3173 1.63311Z"
                  fill={pinkStroke}
                  stroke={triangleStroke}
                />
              </g>
            )
          })}
        </svg>
      )}
      {tooltip.content && <PerformanceScoreTooltip x={tooltip.x} y={tooltip.y} content={tooltip.content} />}
    </PizzaChartWrapper>
  )
}

export default PerformanceScoreChart
